<script setup lang="ts">
import { ref, computed, watch, onMounted, onBeforeUnmount, nextTick } from 'vue'
import type EventBase from '@/model/EventClass/EventBase'
import EventID from '@/model/EventClass/EventID'
import { eventListForegroundColor } from '@/events/list/eventListColor'
import { throttle } from '@/control/throttle'
import {
  findFoldableEndIndex,
  getStructureBlockRange,
  getStructureSelectionContext,
  isChoiceBlockDropTargetAllowed,
  isStructureEndCode,
  isSingleLineEventSelection,
  isStructureOptionCode,
  isStructureReadonlyCode,
} from '@/events/list/structureRules'
import { isCoarsePointerDevice } from '@/platform/viewport/layoutViewport'

const props = defineProps<{
  events: EventBase[]
  loading: boolean
  revision: number
  labelRevision: number
  selectedIndex: number | null
  selectedIndices: Set<number>
  selectedAddKey?: string | null
  collapsedIndices: Set<number>
  canDropAt?: (targetIndex: number, position: 'before' | 'after') => boolean
}>()

const emit = defineEmits<{
  select: [payload: { index: number; append: boolean; range: boolean }]
  'select-add': [payload: { key: string; insertAt: number; previousIndex: number }]
  'toggle-collapse': [index: number]
  contextmenu: [payload: {
    x: number
    y: number
    index?: number
    addPlaceholder?: { key: string; insertAt: number; previousIndex: number }
  }]
  move: [payload: { targetIndex: number; position: 'before' | 'after' }]
}>()

const ROW_H = 28
const TEXT_ROW_LINE_H = ROW_H
const TEXT_ROW_MAX_LINES = 2
const BUFFER = 8
const RANGE_SELECT_SCROLL_EDGE = 24
const RANGE_SELECT_SCROLL_STEP = 14
const ROW_LONG_PRESS_MS = 420
const ROW_POINTER_DRAG_MOVE_THRESHOLD = 10

const scrollEl = ref<HTMLElement | null>(null)
const viewportH = ref(320)
const scrollTop = ref(0)
const dragOverIndex = ref<number | null>(null)
const dragOverPosition = ref<'before' | 'after'>('after')
const rangeSelectPointerId = ref<number | null>(null)
const rowPointerDragActive = ref(false)
const canUseNativeRowDrag = ref(true)
let lastTouchContextAt = 0
let lastRangeSelectClientY = 0
let rangeSelectRaf = 0
let rangeSelectCaptureEl: HTMLElement | null = null
let rangeSelectAutoScrolling = false
let listScrollLockedTop = 0
let rowPointerDragPointerId: number | null = null
let rowPointerDragSourceIndex = -1
let rowPointerDragStartX = 0
let rowPointerDragStartY = 0
let rowLongPressTimer = 0

type EventDisplayRow = {
  kind: 'event'
  key: string
  realIndex: number
  indent: number
}

type AddDisplayRow = {
  kind: 'add'
  key: string
  insertAt: number
  previousIndex: number
  nextIndex: number
  indent: number
}

type DisplayRow = EventDisplayRow | AddDisplayRow
type RenderDisplayRow = DisplayRow & {
  rowInVisible: number
  rowHeight: number
  text: string
  isTextEvent: boolean
  canFold: boolean
  isCollapsed: boolean
  isSelected: boolean
  isStructuralShell: boolean
  canOperate: boolean
  textColor: string
}

type FoldEndCacheItem = {
  event: EventBase
  revision: number
  endIndex: number
}

const foldEndCache = new Map<number, FoldEndCacheItem>()

function buildAddRowBefore(realIndex: number): AddDisplayRow | null {
  const anchor = props.events[realIndex]
  if (!anchor) return null
  const code = anchor.Code as EventID
  const indent = Math.max(0, anchor.Indent ?? 0)
  if ((code === EventID.IF || code === EventID.Loop) && props.collapsedIndices.has(realIndex)) {
    return null
  }
  if (
    code !== EventID.TextChoice
    && code !== EventID.ButtonDifChoose
    && code !== EventID.IF
    && code !== EventID.IfChoice
    && code !== EventID.Loop
  ) {
    return null
  }
  const context = getStructureSelectionContext(props.events, realIndex)
  if (context.kind !== 'header' && context.kind !== 'option') return null

  const range = getStructureBlockRange(props.events, context.headerIndex)
  if (!range) return null

  let boundaryIndex = context.endIndex
  for (let index = realIndex + 1; index <= context.endIndex; index += 1) {
    const event = props.events[index]
    if (!event) continue
    const eventCode = event.Code as EventID
    const eventIndent = Math.max(0, event.Indent ?? 0)
    const isSameLevelOption = context.rule.options.includes(eventCode)
      && (eventIndent === range.optionIndent || eventIndent === range.headerIndent)
    const isSameLevelEnd = context.rule.ends.includes(eventCode)
      && eventIndent === range.headerIndent
    if (isSameLevelOption || isSameLevelEnd) {
      boundaryIndex = index
      break
    }
  }

  const insertAt = Math.max(realIndex + 1, boundaryIndex)
  const previousIndex = insertAt - 1
  return {
    kind: 'add',
    key: `add-after-branch-${realIndex}`,
    insertAt,
    previousIndex,
    nextIndex: boundaryIndex < props.events.length ? boundaryIndex : -1,
    indent: indent + 1,
  }
}

const displayRows = computed(() => {
  props.revision
  const addRowsBefore = new Map<number, AddDisplayRow[]>()
  const tailAddRows: AddDisplayRow[] = []
  const rows: DisplayRow[] = []
  for (let realIndex = 0; realIndex < props.events.length; realIndex += 1) {
    if ((props.events[realIndex]?.FoldIndent ?? 0) > 0) continue
    const addRows = addRowsBefore.get(realIndex)
    if (addRows?.length) rows.push(...addRows)
    const addRow = buildAddRowBefore(realIndex)
    if (addRow) {
      if (addRow.insertAt >= props.events.length) {
        tailAddRows.push(addRow)
      } else {
        const bucket = addRowsBefore.get(addRow.insertAt) ?? []
        bucket.push(addRow)
        addRowsBefore.set(addRow.insertAt, bucket)
      }
    }
    rows.push({
      kind: 'event',
      key: `event-${realIndex}`,
      realIndex,
      indent: Math.max(0, props.events[realIndex]?.Indent ?? 0),
    })
  }
  if (tailAddRows.length) rows.push(...tailAddRows)
  rows.push({
    kind: 'add',
    key: 'add-end',
    insertAt: props.events.length,
    previousIndex: props.events.length - 1,
    nextIndex: -1,
    indent: 0,
  })
  return rows
})

function normalizeEventListText(value: string): string {
  return String(value || '').split('\\n').join('\n').replace(/\r\n/g, '\n')
}

function getTextEventListText(event: EventBase): string {
  const summary = event.content() ?? ''
  if (event.Code !== EventID.Text) return summary
  const raw = normalizeEventListText(event.Parameter?.[2] ?? '')
  if (!raw) return summary
  const label = summary.includes('：') ? summary.split('：')[0] : '文本'
  return `${label}：${raw}`
}

function getDisplayRowHeight(row: DisplayRow): number {
  if (row.kind === 'add') return ROW_H
  const event = props.events[row.realIndex]
  if (event?.Code !== EventID.Text) return ROW_H
  const raw = normalizeEventListText(event.Parameter?.[2] ?? '')
  const lineCount = Math.min(TEXT_ROW_MAX_LINES, Math.max(1, raw.split('\n').length))
  return lineCount * TEXT_ROW_LINE_H
}

const rowHeights = computed(() => {
  props.labelRevision
  return displayRows.value.map((row) => getDisplayRowHeight(row))
})

const rowOffsets = computed(() => {
  const offsets = [0]
  for (const height of rowHeights.value) {
    offsets.push((offsets[offsets.length - 1] ?? 0) + height)
  }
  return offsets
})

const totalHeight = computed(() => rowOffsets.value[rowOffsets.value.length - 1] ?? 0)

function findRowIndexByOffset(offset: number): number {
  const offsets = rowOffsets.value
  let left = 0
  let right = Math.max(0, offsets.length - 2)
  while (left <= right) {
    const mid = Math.floor((left + right) / 2)
    const start = offsets[mid] ?? 0
    const end = offsets[mid + 1] ?? start
    if (offset < start) {
      right = mid - 1
    } else if (offset >= end) {
      left = mid + 1
    } else {
      return mid
    }
  }
  return Math.max(0, Math.min(offsets.length - 2, left))
}

const sliceRange = computed(() => {
  const rows = displayRows.value
  const vh = Math.max(viewportH.value, ROW_H)
  const start = Math.max(0, findRowIndexByOffset(scrollTop.value) - BUFFER)
  const visibleBottom = scrollTop.value + vh
  const visibleEnd = Math.min(rows.length, findRowIndexByOffset(visibleBottom) + 1 + BUFFER)
  const end = Math.max(start, visibleEnd)
  return { start, end, rows }
})

const padTop = computed(() => rowOffsets.value[sliceRange.value.start] ?? 0)
const padBottom = computed(() => {
  const { end, rows } = sliceRange.value
  return Math.max(0, totalHeight.value - (rowOffsets.value[Math.min(end, rows.length)] ?? 0))
})

const visibleSlice = computed(() => {
  props.labelRevision
  props.selectedIndex
  props.selectedIndices
  const { start, end, rows } = sliceRange.value
  return rows.slice(start, end).map((row, i): RenderDisplayRow => {
    const rowInVisible = start + i
    if (row.kind === 'add') {
      const isSelected = isAddRowSelected(row)
      return {
        ...row,
        rowInVisible,
        rowHeight: ROW_H,
        text: '>',
        isTextEvent: false,
        canFold: false,
        isCollapsed: false,
        isSelected,
        isStructuralShell: false,
        canOperate: false,
        textColor: isSelected
          ? 'var(--scene-event-selected-text)'
          : 'var(--theme-text-secondary)',
      }
    }
    const isSelected = isEventRowSelected(row.realIndex)
    const event = props.events[row.realIndex]
    const text = event ? getTextEventListText(event) : ''
    const isTextEvent = event?.Code === EventID.Text
    return {
      ...row,
      rowInVisible,
      rowHeight: rowHeights.value[rowInVisible] ?? ROW_H,
      text,
      isTextEvent,
      canFold: hasFoldableChildren(row.realIndex),
      isCollapsed: isCollapsed(row.realIndex),
      isSelected,
      isStructuralShell: isStructuralShellEvent(row.realIndex),
      canOperate: canOperateRow(row.realIndex),
      textColor: !event
        ? 'var(--theme-text-primary)'
        : isSelected
          ? 'var(--scene-event-selected-text)'
          : eventListForegroundColor(event),
    }
  })
})

function isCollapsed(realIndex: number): boolean {
  return props.collapsedIndices.has(realIndex)
}

function hasFoldableChildren(realIndex: number): boolean {
  const code = props.events[realIndex]?.Code ?? -1
  if (isStructureReadonlyCode(code)) return false
  return getFoldableEndIndex(realIndex) > realIndex
}

function getFoldableEndIndex(realIndex: number): number {
  const event = props.events[realIndex]
  if (!event) return realIndex
  const cached = foldEndCache.get(realIndex)
  if (cached?.event === event && cached.revision === props.revision) return cached.endIndex
  const endIndex = findFoldableEndIndex(props.events, realIndex)
  foldEndCache.set(realIndex, {
    event,
    revision: props.revision,
    endIndex,
  })
  return endIndex
}

function applyScrollTop(e: Event) {
  const t = e.target as HTMLElement
  const isGestureLocked = rangeSelectPointerId.value !== null || rowPointerDragActive.value
  if (isGestureLocked && !rangeSelectAutoScrolling) {
    if (t.scrollTop !== listScrollLockedTop) {
      t.scrollTop = listScrollLockedTop
    }
    return
  }
  scrollTop.value = t.scrollTop
  if (isGestureLocked) {
    listScrollLockedTop = t.scrollTop
  }
}

const onScroll = throttle(applyScrollTop, 100)

const onResizeViewport = throttle(() => {
  if (scrollEl.value) viewportH.value = scrollEl.value.clientHeight
}, 100)

let ro: ResizeObserver | null = null

onMounted(() => {
  canUseNativeRowDrag.value = !isCoarsePointerDevice()
  nextTick(() => {
    const el = scrollEl.value
    if (!el) return
    viewportH.value = el.clientHeight
    ro = new ResizeObserver(() => {
      onResizeViewport()
    })
    ro.observe(el)
  })
})

onBeforeUnmount(() => {
  ro?.disconnect()
  ro = null
  forceFinishRangeSelect()
  forceFinishRowPointerDrag()
})

watch(
  () => [props.events.length, props.revision, props.labelRevision] as const,
  () => {
    nextTick(() => {
      if (scrollEl.value) scrollTop.value = scrollEl.value.scrollTop
    })
  },
)

function indentPx(indent: number) {
  return Math.max(0, indent) * 12
}

function onRowClick(row: DisplayRow, event: MouseEvent) {
  if (row.kind === 'add') {
    emit('select-add', {
      key: row.key,
      insertAt: row.insertAt,
      previousIndex: row.previousIndex,
    })
    return
  }
  emitEventSelect(row.realIndex, event.ctrlKey || event.metaKey, event.shiftKey)
}

function onRowDoubleClick(row: DisplayRow, event: MouseEvent) {
  if (row.kind !== 'event') return
  event.preventDefault()
  if (!hasFoldableChildren(row.realIndex)) return
  emit('toggle-collapse', row.realIndex)
}

function onToggle(realIndex: number, e: MouseEvent) {
  e.stopPropagation()
  e.preventDefault()
  emit('toggle-collapse', realIndex)
}

function openRowContextMenu(row: DisplayRow, x: number, y: number) {
  if (row.kind === 'add') {
    emit('contextmenu', {
      x,
      y,
      addPlaceholder: {
        key: row.key,
        insertAt: row.insertAt,
        previousIndex: row.previousIndex,
      },
    })
    return
  }
  const realIndex = row.realIndex
  if (!canOperateRow(realIndex)) return
  if (!props.selectedIndices.has(realIndex)) {
    emitEventSelect(realIndex)
  }
  emit('contextmenu', { index: realIndex, x, y })
}

function onRowContextMenu(row: DisplayRow, event: MouseEvent) {
  if (isTouchGeneratedContextMenu(event)) return
  openRowContextMenu(row, event.clientX, event.clientY)
}

function isTouchGeneratedContextMenu(event: MouseEvent): boolean {
  const sourceCapabilities = (event as MouseEvent & { sourceCapabilities?: { firesTouchEvents?: boolean } }).sourceCapabilities
  return Boolean(sourceCapabilities?.firesTouchEvents) || performance.now() - lastTouchContextAt < 900
}

function rememberTouchContext(event: PointerEvent): void {
  if (event.pointerType !== 'mouse') {
    lastTouchContextAt = performance.now()
  }
}

function onMoreButtonClick(row: DisplayRow, event: MouseEvent) {
  event.preventDefault()
  event.stopPropagation()
  const rect = (event.currentTarget as HTMLElement).getBoundingClientRect()
  openRowContextMenu(row, rect.right, rect.bottom)
}

function emitEventSelect(index: number, append = false, range = false) {
  emit('select', { index, append, range })
}

/** 将展示行解析为可参与拖选的事件下标。 */
function resolveEventIndexFromDisplayRow(row: DisplayRow | undefined): number | null {
  if (!row) return null
  if (row.kind === 'event') return row.realIndex
  if (row.previousIndex >= 0) return row.previousIndex
  if (row.nextIndex >= 0) return row.nextIndex
  return null
}

/** 根据指针纵坐标换算事件下标（虚拟列表坐标系）。 */
function resolveEventIndexFromClientY(clientY: number): number | null {
  const el = scrollEl.value
  if (!el) return null
  const offsetY = clientY - el.getBoundingClientRect().top + el.scrollTop
  const rows = displayRows.value
  if (!rows.length) return null
  if (offsetY <= 0) return resolveEventIndexFromDisplayRow(rows[0])
  if (offsetY >= totalHeight.value) {
    for (let i = rows.length - 1; i >= 0; i -= 1) {
      const index = resolveEventIndexFromDisplayRow(rows[i])
      if (index !== null) return index
    }
    return props.events.length > 0 ? props.events.length - 1 : null
  }
  return resolveEventIndexFromDisplayRow(rows[findRowIndexByOffset(offsetY)])
}

function emitRangeSelectAtClientY(clientY: number) {
  const index = resolveEventIndexFromClientY(clientY)
  if (index !== null) emitEventSelect(index, false, true)
}

type RangeSelectScrollEdge = -1 | 0 | 1

function getRangeSelectScrollEdge(clientY: number, rect: DOMRect): RangeSelectScrollEdge {
  if (clientY < rect.top + RANGE_SELECT_SCROLL_EDGE) return -1
  if (clientY > rect.bottom - RANGE_SELECT_SCROLL_EDGE) return 1
  return 0
}

function scrollListByStep(edge: RangeSelectScrollEdge): boolean {
  if (!edge) return false
  const el = scrollEl.value
  if (!el) return false
  const maxScroll = Math.max(0, el.scrollHeight - el.clientHeight)
  const next = edge < 0
    ? Math.max(0, el.scrollTop - RANGE_SELECT_SCROLL_STEP)
    : Math.min(maxScroll, el.scrollTop + RANGE_SELECT_SCROLL_STEP)
  if (next === el.scrollTop) return false
  rangeSelectAutoScrolling = true
  el.scrollTop = next
  scrollTop.value = next
  requestAnimationFrame(() => {
    rangeSelectAutoScrolling = false
  })
  return true
}

function isRangeSelectNearScrollEdge(clientY: number): boolean {
  const rect = scrollEl.value?.getBoundingClientRect()
  return rect ? getRangeSelectScrollEdge(clientY, rect) !== 0 : false
}

function maybeAutoScrollDuringRangeSelect(clientY: number): boolean {
  const el = scrollEl.value
  const rect = el?.getBoundingClientRect()
  if (!el || !rect) return false
  return scrollListByStep(getRangeSelectScrollEdge(clientY, rect))
}

/** 拖选/触屏拖拽排序期间锁定列表滚动，避免与手势冲突。 */
function lockListScroll(): void {
  const el = scrollEl.value
  if (!el) return
  listScrollLockedTop = el.scrollTop
  el.style.overflow = 'hidden'
  el.style.touchAction = 'none'
}

/** 解除列表滚动锁定。 */
function unlockListScroll(): void {
  const el = scrollEl.value
  if (!el) return
  if (rangeSelectPointerId.value !== null || rowPointerDragActive.value) return
  el.style.overflow = ''
  el.style.touchAction = ''
}

/** 根据指针纵坐标更新拖拽落点指示。 */
function updateDragOverFromClientY(clientY: number): void {
  const el = scrollEl.value
  if (!el) return
  const offsetY = clientY - el.getBoundingClientRect().top + el.scrollTop
  const displayRowIndex = findRowIndexByOffset(offsetY)
  const row = displayRows.value[displayRowIndex]
  if (!row || row.kind !== 'event') {
    clearDragOverState()
    return
  }
  const realIndex = row.realIndex
  const rowStart = rowOffsets.value[displayRowIndex] ?? 0
  const rowEnd = rowOffsets.value[displayRowIndex + 1] ?? rowStart
  const position = offsetY < rowStart + (rowEnd - rowStart) / 2 ? 'before' : 'after'
  if (!isChoiceBlockDropTargetAllowed(props.events, realIndex, position)) {
    clearDragOverState()
    return
  }
  dragOverIndex.value = realIndex
  dragOverPosition.value = position
}

function clearRowLongPressTimer(): void {
  if (!rowLongPressTimer) return
  window.clearTimeout(rowLongPressTimer)
  rowLongPressTimer = 0
}

function clearRowPointerDragWindowListeners(): void {
  window.removeEventListener('pointermove', onWindowRowPointerMove)
  window.removeEventListener('pointerup', onWindowRowPointerUp)
  window.removeEventListener('pointercancel', onWindowRowPointerUp)
}

/** 准备触屏拖拽排序的选中集（与 HTML5 dragstart 一致）。 */
function prepareRowDragSource(realIndex: number): boolean {
  const hasOperableSelection = [...props.selectedIndices].some((index) => canOperateRow(index))
  if (!canOperateRow(realIndex) && !hasOperableSelection) return false
  if (!props.selectedIndices.has(realIndex)) {
    emitEventSelect(realIndex)
  }
  return true
}

function activateRowPointerDrag(clientY: number): void {
  if (!prepareRowDragSource(rowPointerDragSourceIndex)) return
  rowPointerDragActive.value = true
  lastRangeSelectClientY = clientY
  lockListScroll()
  updateDragOverFromClientY(clientY)
}

function forceFinishRowPointerDrag(): void {
  clearRowLongPressTimer()
  clearRowPointerDragWindowListeners()
  rowPointerDragPointerId = null
  rowPointerDragActive.value = false
  rowPointerDragSourceIndex = -1
  clearDragOverState()
  stopRangeSelectLoop()
  unlockListScroll()
}

function onWindowRowPointerMove(event: PointerEvent): void {
  if (rowPointerDragPointerId !== event.pointerId) return
  const dx = event.clientX - rowPointerDragStartX
  const dy = event.clientY - rowPointerDragStartY
  if (!rowPointerDragActive.value) {
    if (Math.hypot(dx, dy) >= ROW_POINTER_DRAG_MOVE_THRESHOLD) {
      clearRowLongPressTimer()
    }
    return
  }
  event.preventDefault()
  lastRangeSelectClientY = event.clientY
  updateDragOverFromClientY(event.clientY)
  scheduleRangeSelectAutoScroll()
}

function onWindowRowPointerUp(event: PointerEvent): void {
  if (rowPointerDragPointerId !== event.pointerId) return
  const wasActive = rowPointerDragActive.value
  const targetIndex = dragOverIndex.value
  const position = dragOverPosition.value
  forceFinishRowPointerDrag()
  if (wasActive && targetIndex !== null && isChoiceBlockDropTargetAllowed(props.events, targetIndex, position)) {
    emit('move', { targetIndex, position })
  }
}

/** 触屏事件行：长按后拖动排序；短滑仍交给列表滚动。 */
function onBodyPointerDown(row: DisplayRow, event: PointerEvent): void {
  if (canUseNativeRowDrag.value) return
  if (row.kind !== 'event' || !canOperateRow(row.realIndex)) return
  if (event.button !== 0) return
  const target = event.target
  if (target instanceof Element && (
    target.closest('.scene-event-fold')
    || target.closest('.scene-event-more')
  )) return
  forceFinishRangeSelect()
  forceFinishRowPointerDrag()
  rowPointerDragPointerId = event.pointerId
  rowPointerDragSourceIndex = row.realIndex
  rowPointerDragStartX = event.clientX
  rowPointerDragStartY = event.clientY
  lastRangeSelectClientY = event.clientY
  clearRowLongPressTimer()
  rowLongPressTimer = window.setTimeout(() => {
    rowLongPressTimer = 0
    if (rowPointerDragPointerId !== event.pointerId) return
    activateRowPointerDrag(lastRangeSelectClientY)
  }, ROW_LONG_PRESS_MS)
  window.addEventListener('pointermove', onWindowRowPointerMove)
  window.addEventListener('pointerup', onWindowRowPointerUp)
  window.addEventListener('pointercancel', onWindowRowPointerUp)
}

function onRangeSelectFrame() {
  if (rangeSelectPointerId.value === null) {
    rangeSelectRaf = 0
    return
  }
  if (maybeAutoScrollDuringRangeSelect(lastRangeSelectClientY)) {
    emitRangeSelectAtClientY(lastRangeSelectClientY)
  }
  rangeSelectRaf = isRangeSelectNearScrollEdge(lastRangeSelectClientY)
    ? requestAnimationFrame(onRangeSelectFrame)
    : 0
}

function scheduleRangeSelectAutoScroll() {
  if (rangeSelectRaf || rangeSelectPointerId.value === null) return
  if (isRangeSelectNearScrollEdge(lastRangeSelectClientY)) {
    rangeSelectRaf = requestAnimationFrame(onRangeSelectFrame)
  }
}

function stopRangeSelectLoop() {
  if (!rangeSelectRaf) return
  cancelAnimationFrame(rangeSelectRaf)
  rangeSelectRaf = 0
}

function clearRangeSelectWindowListeners(): void {
  window.removeEventListener('pointermove', onWindowRangeSelectPointerMove)
  window.removeEventListener('pointerup', onWindowRangeSelectPointerUp)
  window.removeEventListener('pointercancel', onWindowRangeSelectPointerUp)
}

/** 强制结束行号拖选，避免虚拟列表卸载捕获行后拖选状态残留。 */
function forceFinishRangeSelect(): void {
  const pointerId = rangeSelectPointerId.value
  rangeSelectPointerId.value = null
  stopRangeSelectLoop()
  if (rangeSelectCaptureEl) {
    rangeSelectCaptureEl.removeEventListener('lostpointercapture', onRangeSelectCaptureLost)
    if (pointerId !== null && rangeSelectCaptureEl.hasPointerCapture(pointerId)) {
      try {
        rangeSelectCaptureEl.releasePointerCapture(pointerId)
      } catch {
        /* ignore release failure */
      }
    }
    rangeSelectCaptureEl = null
  }
  clearRangeSelectWindowListeners()
  unlockListScroll()
}

function finishRangeSelect(event: PointerEvent): void {
  if (rangeSelectPointerId.value !== event.pointerId) return
  forceFinishRangeSelect()
}

function onRangeSelectCaptureLost(event: PointerEvent): void {
  finishRangeSelect(event)
}

function onWindowRangeSelectPointerMove(event: PointerEvent): void {
  if (rangeSelectPointerId.value !== event.pointerId) return
  lastRangeSelectClientY = event.clientY
  emitRangeSelectAtClientY(event.clientY)
  scheduleRangeSelectAutoScroll()
}

function onWindowRangeSelectPointerUp(event: PointerEvent): void {
  finishRangeSelect(event)
}

/** 行号列 touchstart：iOS 需在 touch 阶段阻止默认滚动，pointerdown 单独 prevent 不够。 */
function onIdxTouchStart(event: TouchEvent): void {
  event.preventDefault()
  event.stopPropagation()
}

/** 行号列按下：进入拖选多选模式（与 Shift 连选规则一致）。 */
function onIdxPointerDown(row: DisplayRow, event: PointerEvent) {
  if (row.kind !== 'event' || event.button !== 0) return
  event.preventDefault()
  event.stopPropagation()
  forceFinishRowPointerDrag()
  forceFinishRangeSelect()
  lastRangeSelectClientY = event.clientY
  rangeSelectPointerId.value = event.pointerId
  lockListScroll()
  emitEventSelect(row.realIndex)
  const target = event.currentTarget as HTMLElement
  rangeSelectCaptureEl = target
  target.setPointerCapture(event.pointerId)
  target.addEventListener('lostpointercapture', onRangeSelectCaptureLost)
  window.addEventListener('pointermove', onWindowRangeSelectPointerMove)
  window.addEventListener('pointerup', onWindowRangeSelectPointerUp)
  window.addEventListener('pointercancel', onWindowRangeSelectPointerUp)
}

/** 在事件正文区按下时结束拖选，避免滚动被误判为连选。 */
function onScrollContainerPointerDown(event: PointerEvent) {
  const target = event.target
  if (!(target instanceof Element)) return
  if (target.closest('.scene-event-idx:not(.scene-event-idx--empty)')) return
  forceFinishRangeSelect()
}

function onRowDragStart(row: DisplayRow, event: DragEvent) {
  if (row.kind !== 'event') {
    event.preventDefault()
    return
  }
  const realIndex = row.realIndex
  const hasOperableSelection = [...props.selectedIndices].some((index) => canOperateRow(index))
  if (!canOperateRow(realIndex) && !hasOperableSelection) {
    event.preventDefault()
    return
  }
  if (!props.selectedIndices.has(realIndex)) {
    emitEventSelect(realIndex)
  }
  if (event.dataTransfer) {
    event.dataTransfer.effectAllowed = 'move'
    event.dataTransfer.setData('text/plain', String(realIndex))
  }
}

function isDropTargetAllowed(realIndex: number, position: 'before' | 'after'): boolean {
  if (props.canDropAt) return props.canDropAt(realIndex, position)
  return isChoiceBlockDropTargetAllowed(props.events, realIndex, position)
}

function onRowDragOver(row: DisplayRow, event: DragEvent) {
  if (row.kind !== 'event') return
  const realIndex = row.realIndex
  event.preventDefault()
  const rowEl = event.currentTarget as HTMLElement | null
  if (!rowEl) return
  const rect = rowEl.getBoundingClientRect()
  const position = event.clientY < rect.top + rect.height / 2 ? 'before' : 'after'
  if (!isDropTargetAllowed(realIndex, position)) {
    clearDragOverState()
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'none'
    }
    return
  }
  dragOverIndex.value = realIndex
  dragOverPosition.value = position
  if (event.dataTransfer) {
    event.dataTransfer.dropEffect = 'move'
  }
}

function clearDragOverState() {
  dragOverIndex.value = null
  dragOverPosition.value = 'after'
}

function onRowDrop(row: DisplayRow, event: DragEvent) {
  if (row.kind !== 'event') return
  const realIndex = row.realIndex
  event.preventDefault()
  if (!isDropTargetAllowed(realIndex, dragOverPosition.value)) {
    clearDragOverState()
    return
  }
  emit('move', { targetIndex: realIndex, position: dragOverPosition.value })
  clearDragOverState()
}

function isStructuralShellEvent(realIndex: number): boolean {
  const code = props.events[realIndex]?.Code ?? -1
  return isStructureOptionCode(code) || isStructureEndCode(code)
}

function canOperateRow(realIndex: number): boolean {
  return !isStructuralShellEvent(realIndex)
}

function isPrimarySingleLineSelection(): boolean {
  const selectedIndex = props.selectedIndex
  return props.selectedIndices.size <= 1
    && selectedIndex !== null
    && isSingleLineEventSelection(props.events, selectedIndex)
}

function isEventRowSelected(realIndex: number): boolean {
  if (props.selectedIndices.size > 1) {
    return props.selectedIndices.has(realIndex)
  }
  if (isPrimarySingleLineSelection()) {
    return props.selectedIndex === realIndex
  }
  return props.selectedIndices.has(realIndex)
}

function isAddRowSelected(row: AddDisplayRow): boolean {
  if (props.selectedAddKey === row.key) return true
  if (isPrimarySingleLineSelection()) return false
  if (!props.selectedIndices.size) return false
  if (row.nextIndex < 0) return false
  const previousSelected = row.previousIndex >= 0 && props.selectedIndices.has(row.previousIndex)
  const nextSelected = row.nextIndex >= 0 && props.selectedIndices.has(row.nextIndex)
  return previousSelected && nextSelected
}

</script>

<template>
  <div
    class="scene-event-list"
    :class="{
      'scene-event-list--range-select': rangeSelectPointerId !== null,
      'scene-event-list--pointer-lock': rangeSelectPointerId !== null || rowPointerDragActive,
    }"
    @contextmenu.prevent
  >
    <div v-if="loading" class="scene-loading">正在加载当前场景事件…</div>
    <div
      v-else
      ref="scrollEl"
      class="scene-event-scroll"
      @scroll.passive="onScroll"
      @pointerdown="onScrollContainerPointerDown"
    >
      <div class="scene-event-virtual" :style="{ height: totalHeight + 'px' }">
        <div
          class="scene-event-virtual-inner"
          :style="{
            paddingTop: padTop + 'px',
            paddingBottom: padBottom + 'px',
          }"
        >
          <div
            v-for="item in visibleSlice"
            :key="item.key"
            class="scene-event-row"
            :class="{
              sel: item.isSelected,
              'sel-primary': item.kind === 'event' && selectedIndex === item.realIndex,
              'scene-event-row--structural-shell': item.isStructuralShell,
              'scene-event-row--add': item.kind === 'add',
              'stripe-a': item.rowInVisible % 2 === 0,
              'stripe-b': item.rowInVisible % 2 === 1,
              'drop-before': item.kind === 'event' && dragOverIndex === item.realIndex && dragOverPosition === 'before',
              'drop-after': item.kind === 'event' && dragOverIndex === item.realIndex && dragOverPosition === 'after',
            }"
            :style="{
              height: item.rowHeight + 'px',
              color: item.textColor,
            }"
            @pointerdown="rememberTouchContext"
            @contextmenu.prevent="onRowContextMenu(item, $event)"
            @dragover="onRowDragOver(item, $event)"
            @dragleave="clearDragOverState"
            @drop="onRowDrop(item, $event)"
            @dragend="clearDragOverState"
          >
            <span
              v-if="item.kind === 'event'"
              class="scene-event-idx"
              title="拖动选择多行"
              @touchstart.stop="onIdxTouchStart"
              @pointerdown="onIdxPointerDown(item, $event)"
            >{{ item.realIndex + 1 }}</span>
            <span v-else class="scene-event-idx scene-event-idx--empty" />
            <div
              class="scene-event-body"
              :draggable="item.kind === 'event' && item.canOperate && canUseNativeRowDrag"
              @pointerdown="onBodyPointerDown(item, $event)"
              @click="onRowClick(item, $event)"
              @dblclick="onRowDoubleClick(item, $event)"
              @dragstart="onRowDragStart(item, $event)"
            >
              <span class="scene-event-gutter" :style="{ width: indentPx(item.indent) + 'px' }" />
              <button
                v-if="item.kind === 'event' && item.canFold"
                type="button"
                class="scene-event-fold"
                :aria-expanded="!item.isCollapsed"
                :title="item.isCollapsed ? '展开' : '折叠'"
                @click="onToggle(item.realIndex, $event)"
                @dblclick.stop.prevent
              >
                {{ item.isCollapsed ? '▶' : '▼' }}
              </button>
              <span v-else class="scene-event-fold-spacer" />
              <span
                class="scene-event-name"
                :class="{
                  'scene-event-name--multi': item.isTextEvent && item.rowHeight > ROW_H,
                  'scene-event-name--text': item.isTextEvent && item.rowHeight <= ROW_H,
                }"
                :title="item.text"
              >
                {{ item.text }}
              </span>
              <span
                v-if="(item.kind === 'event' && item.canOperate) || item.kind === 'add'"
                class="scene-event-more"
                role="button"
                tabindex="0"
                title="更多操作"
                aria-label="更多操作"
                @click="onMoreButtonClick(item, $event)"
                @pointerdown.stop
              >
                更多
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.scene-event-list {
  --scene-event-panel-bg: var(--theme-bg-panel);
  --scene-event-row-a: var(--theme-list-row-a);
  --scene-event-row-b: var(--theme-list-row-b);
  --scene-event-row-a-hover: var(--theme-list-row-a-hover);
  --scene-event-row-b-hover: var(--theme-list-row-b-hover);
  --scene-event-selected-bg: var(--theme-accent);
  --scene-event-selected-marker: var(--theme-accent-strong);
  --scene-event-selected-text: var(--theme-text-inverse);
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  background: var(--scene-event-panel-bg);
}

.scene-loading {
  margin: var(--app-space-xs);
  padding: var(--app-space-sm) var(--app-space-md);
  color: var(--theme-text-secondary);
  font-size: var(--app-font-size-sm);
  background: var(--theme-bg-secondary);
  border: 0;
}

.scene-event-scroll {
  flex: 1;
  min-height: 120px;
  overflow: auto;
  border: 0;
  background: var(--scene-event-panel-bg);
  transform: translateZ(0);
}

.scene-event-virtual {
  position: relative;
}

.scene-event-virtual-inner {
  box-sizing: border-box;
}

.scene-event-list--range-select {
  cursor: default;
}

.scene-event-list--pointer-lock .scene-event-scroll {
  touch-action: none;
}

.scene-event-row {
  display: flex;
  align-items: flex-start;
  gap: 0;
  padding: 0 var(--app-space-xs) 0 3px;
  box-sizing: border-box;
  font-size: var(--app-font-size-sm);
  user-select: none;
  -webkit-touch-callout: none;
  color: var(--theme-text-primary);
}

.scene-event-body {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: flex-start;
  gap: 1px;
  cursor: pointer;
}

.scene-event-row.stripe-a {
  background: var(--scene-event-row-a);
}

.scene-event-row.stripe-b {
  background: var(--scene-event-row-b);
}

.scene-event-row.stripe-a:hover:not(.sel) {
  background: var(--scene-event-row-a-hover);
}

.scene-event-row.stripe-b:hover:not(.sel) {
  background: var(--scene-event-row-b-hover);
}

.scene-event-row--add .scene-event-name {
  color: inherit;
}

.scene-event-row--structural-shell .scene-event-name {
  font-weight: 600;
}

.scene-event-row.sel,
.scene-event-row.sel-primary {
  background: var(--scene-event-selected-bg);
  color: var(--scene-event-selected-text);
  box-shadow: inset 3px 0 0 var(--scene-event-selected-marker);
}

.scene-event-row.sel .scene-event-idx,
.scene-event-row.sel-primary .scene-event-idx {
  border-right-color: var(--scene-event-selected-marker);
}

.scene-event-row.sel .scene-event-fold,
.scene-event-row.sel-primary .scene-event-fold {
  border-color: var(--scene-event-selected-marker);
  background: var(--scene-event-selected-bg);
  color: var(--scene-event-selected-text);
}

.scene-event-row.drop-before {
  box-shadow: inset 0 2px 0 0 var(--theme-accent);
}

.scene-event-row.drop-after {
  box-shadow: inset 0 -2px 0 0 var(--theme-accent);
}

.scene-event-gutter {
  flex-shrink: 0;
  height: 100%;
}

.scene-event-fold {
  flex-shrink: 0;
  width: 14px;
  height: 22px;
  margin-top: 3px;
  padding: 0;
  border: 1px solid var(--theme-panel-border-soft);
  background: var(--theme-bg-secondary);
  color: var(--theme-text-label);
  font-size: var(--app-font-size-xs);
  line-height: 1;
  cursor: pointer;
}

.scene-event-fold:hover {
  background: var(--theme-bg-splitter-dot);
  color: var(--theme-text-heading);
}

.scene-event-fold-spacer {
  width: 8px;
  flex-shrink: 0;
  height: 28px;
}

.scene-event-idx {
  flex-shrink: 0;
  width: 26px;
  font-family: ui-monospace, monospace;
  font-size: var(--app-font-size-xs);
  text-align: right;
  padding-right: 4px;
  line-height: 28px;
  border-right: 0;
  color: inherit;
  cursor: default;
  touch-action: none;
  -webkit-user-select: none;
  user-select: none;
}

@media (pointer: coarse) {
  .scene-event-idx:not(.scene-event-idx--empty) {
    width: 40px;
    padding-left: 4px;
    padding-right: 6px;
  }
}

.scene-event-idx--empty {
  cursor: default;
}

.scene-event-name {
  flex: 1;
  min-width: 0;
  padding-left: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  line-height: 28px;
  color: inherit;
}

.scene-event-name--text {
  white-space: pre;
}

.scene-event-name--multi {
  display: block;
  white-space: pre;
  line-height: 28px;
  max-height: 56px;
  text-overflow: clip;
}

.scene-event-more {
  all: unset;
  flex: 0 0 auto;
  color: var(--theme-text-very-muted);
  font-size: 10px;
  line-height: 1;
  cursor: pointer;
  display: none;
  margin-top: 9px;
}

.scene-event-more:hover,
.scene-event-more:focus-visible {
  color: var(--theme-text-secondary);
}

.scene-event-row.sel .scene-event-more,
.scene-event-row.sel-primary .scene-event-more {
  color: var(--scene-event-selected-text);
}

@media (hover: hover) and (pointer: fine) {
  .scene-event-row.sel .scene-event-more,
  .scene-event-row.sel-primary .scene-event-more,
  .scene-event-more:focus-visible {
    display: inline-block;
  }
}

.scene-event-row.sel .scene-event-more,
.scene-event-row.sel-primary .scene-event-more {
  display: inline-block;
}
</style>
